import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ITableHeader, TableComponent} from '../../../shared/table/TableComponent';
import {
    Agency,
    AgencyClient,
    AssistanceApplicationType,
    FundTypeClient,
    FundTypeSelection,
    PagedListOfInternalDraftListPreview,
    User,
    UserType
} from '../../../../services/api.client';
import {InternalDraftService} from '../../../../services/internal-draft.service';
import {Router} from '@angular/router';
import {catchError, switchMap, take, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {SecurityService} from '../../../../services/SecurityService';
import {AgencyListModalComponent} from '../../agency/AgencyListModalComponent';
import {AgencyService} from '../../../../services/AgencyService';
import {UntypedFormControl} from '@angular/forms';

@Component({
    selector: 'app-drafts-table',
    templateUrl: './drafts-table.component.html',
    styleUrls: ['./drafts-table.component.scss'],
    standalone: false
})
export class DraftsTableComponent implements OnInit, AfterViewInit {
    @Input() applicationType: AssistanceApplicationType;

    @ViewChild(TableComponent)
    table: TableComponent;

    @ViewChild('agencyListModal')
    agencyListModal: AgencyListModalComponent;
    @Output('onShowAgencyModal') _showAgencies = new EventEmitter();

    draftsList: PagedListOfInternalDraftListPreview;
    searchValue: string;

    currentUser: User;
    currentUser$: Observable<User>;

    selectedAgencyId: string;
    selectedAgencyName = '-- Select Agency --';

    regionAdminAgencies: Agency[];
    regionAdminManagedAgencies: Agency[] = [];
    selectedManagementAgencyId = new UntypedFormControl('');

    fundTypes: FundTypeSelection[] = [];
    selectedFundTypeIds = new UntypedFormControl([]);

    currentSortName = 'EnteredDate';
    currentSorting = true;

    tableHeaders: ITableHeader[] = [
        {text: 'Fund', sortKey: 'PledgeType|Name'},
        {text: 'Last Name', sortKey: 'Client|LastName'},
        {text: 'First Name', sortKey: 'Client|FirstName'},
        {text: 'SSN', sortKey: 'Client|Last4Ssn'},
        {text: 'Created Date', sortKey: 'EnteredDate', ascending: false},
        {text: 'Agency', sortKey: 'User|Agency|AgencyName'},
        {text: 'Created By', sortKey: 'User|FirstName'},
        {text: 'Email', sortKey: 'User|Email'},
    ];

    limit = 10;
    currentPage = 1;
    totalCount = 0;

    constructor(
        private internalDraftService: InternalDraftService,
        private router: Router,
        private _securityService: SecurityService,
        private _agencyService: AgencyService,
        private _agencyClient: AgencyClient,
        private _fundTypeClient: FundTypeClient
    ) {
        this.currentUser$ = this._securityService.currentUser$;
    }

    ngOnInit(): void {
        this.currentUser$.pipe(
            take(1),
            tap(user => {
                this.currentUser = user;
                if (!this.userHasAccessToAllAgencyApplications(user) && user.agencyId) {
                    if (this.hasRegionAdminFilter && user.userType === UserType.RegionAdmin) {
                        this.selectedManagementAgencyId.setValue(user.agencyId);
                    } else {
                        this.selectedAgencyId = user.agencyId;
                        this.selectedAgencyName = user.agency.agencyName;
                    }
                }
            }),
            switchMap(user => {
                if (!this.hasRegionAdminFilter) {
                    return of(null);
                }

                const regionAdminAgencies$ = this._agencyService.getRegionAdminAgencies().pipe(
                    catchError(() => of([])),
                    tap(agencies => {
                        this.regionAdminAgencies = agencies;
                    })
                );

                if (!this.userHasAccessToAllAgencyApplications(user) && user.agencyId && user.userType === UserType.RegionAdmin) {
                    const managedAgencies$ = this._agencyClient.getAgenciesUnderRegionAdmin(user.agencyId, this.applicationType).pipe(
                        catchError(() => of([])),
                        tap(agencies => {
                            this.regionAdminManagedAgencies = agencies;
                        })
                    );
                    return of(null).pipe(switchMap(() => {
                        regionAdminAgencies$.subscribe();
                        return managedAgencies$;
                    }));
                }

                return regionAdminAgencies$;
            }),
            switchMap(() => {
                if (!this.hasFundTypeFilter) {
                    return of(null);
                }

                return this._fundTypeClient.getPledgeTypesByAssistanceApplicationType(this.applicationType, this.currentUser?.agencyId).pipe(
                    catchError(() => of([])),
                    tap(fundTypes => {
                        this.fundTypes = fundTypes.map(ft => ({
                            ...ft,
                            displayName: ft.isActive ? ft.displayName : `${ft.displayName} (Inactive)`
                        }));
                        this.selectedFundTypeIds.setValue([]);
                    })
                );
            })
        ).subscribe();
    }

    ngAfterViewInit(): void {
        this.searchDrafts();
    }

    get isAdmin(): boolean {
        return this.currentUser?.userType === UserType.SystemAdmin;
    }

    get isRegionAdmin(): boolean {
        return this.currentUser?.userType === UserType.RegionAdmin;
    }

    get hasRegionAdminFilter(): boolean {
        return this.applicationType === AssistanceApplicationType.UHONS
            || this.applicationType === AssistanceApplicationType.STLWater;
    }

    get hasFundTypeFilter(): boolean {
        return this.applicationType === AssistanceApplicationType.UHONS;
    }

    setSearchValue(value: string): void {
        this.searchValue = value;
    }

    sortDrafts(options: ITableHeader): void {
        this.currentSortName = options.sortKey;
        this.currentSorting = !options.ascending;
        this.searchDrafts();
    }

    setLimit(limit: number): void {
        this.limit = limit;
        this.searchDrafts();
    }

    setPage(page: number): void {
        setTimeout(() => {
            this.currentPage = page;
            this.searchDrafts();
        });
    }

    searchDrafts(): void {
        const search$ = this.internalDraftService.search(
            this.applicationType,
            this.searchValue,
            this.selectedAgencyId,
            this.selectedManagementAgencyId.value,
            this.selectedFundTypeIds.value,
            this.limit,
            (this.currentPage - 1) * this.limit,
            this.currentSortName,
            this.currentSorting
        ).pipe(
            tap((result) => {
                this.draftsList = result;
                this.totalCount = result.totalCount;
            })
        );
        this.table.request = search$;
    }

    clearFilters(): void {
        this.searchValue = '';
        this.selectedAgencyId = null;
        this.selectedAgencyName = '-- Select Agency --';
        this.selectedManagementAgencyId.setValue('');
        this.selectedFundTypeIds.setValue([]);

        if (!this.userHasAccessToAllAgencyApplications(this.currentUser)) {
            if (this.hasRegionAdminFilter && this.currentUser.userType === UserType.RegionAdmin) {
                this.selectedManagementAgencyId.setValue(this.currentUser.agencyId);
            } else {
                this.selectedAgencyId = this.currentUser.agencyId;
                this.selectedAgencyName = this.currentUser.agency.agencyName;
            }
        }

        this.searchDrafts();
    }

    showAgencyModal(): boolean {
        this.agencyListModal.open();
        return false;
    }

    onAgencySelected(selectedAgency): void {
        if (selectedAgency) {
            selectedAgency.agencyName ? this.selectedAgencyName = selectedAgency.agencyName : this.selectedAgencyName = '';
            this.selectedAgencyId = selectedAgency.id;
        } else {
            this.selectedAgencyName = '';
            this.selectedAgencyId = '';
        }
    }

    isDisabledAgency(): boolean {
        return !this.userHasAccessToAllAgencyApplications(this.currentUser);
    }

    navigateToDraft(draft: any): void {
        this.router.navigate(
            ['app', 'client', draft.clientId, 'assistance-applications', 'add', this.applicationType],
            { queryParams: { draftId: draft.id } }
        ).then();
    }

    private userHasAccessToAllAgencyApplications(user: User): boolean {
        return user.userType === UserType.SystemAdmin;
    }
}
